import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { C, font } from "../theme";
import {
  MAX_GUARDIANS,
  isValidBloodType,
  isValidCpf,
  isValidDateOfBirth,
  isValidEmail,
  isValidEnrollmentDate,
  isValidGuardianName,
  isValidHealthNotes,
  isValidHeightCm,
  isValidNotes,
  isValidPhone,
  isValidState,
  isValidWeightKg,
  isValidZipCode,
} from "../lib/memberValidation";
import { MEMBER_ENROLLMENT_TYPE_LABELS, MEMBER_GENDER_LABELS } from "../lib/enums";
import {
  buildMemberPayload,
  emptyMemberFormValues,
  memberFormValuesFromProfile,
  type AddressFormValue,
  type MemberFormValues,
} from "../lib/memberForm";
import type { CreateMemberRequest, MemberProfileDto, PlanDto } from "../types";
import { AddressFields } from "./AddressFields";
import { Button } from "./Button";
import { Card } from "./Card";
import { DateTimePicker } from "./DateTimePicker";
import { FieldInput, FieldTextarea } from "./Field";
import { GuardianEditor } from "./GuardianEditor";
import { Label } from "./Label";
import { Select } from "./Select";

interface MemberFormProps {
  submitting: boolean;
  submitError: string | null;
  plans: PlanDto[];
  initial?: MemberProfileDto | null;
  editing?: boolean;
  loading?: boolean;
  loadError?: string | null;
  onRetryLoad?: () => void;
  onSubmit: (payload: CreateMemberRequest) => void;
  onCancel: () => void;
}

type TabId = "personal" | "enrollment" | "health" | "guardians";

const TABS: { id: TabId; label: string }[] = [
  { id: "personal", label: "Dados pessoais" },
  { id: "enrollment", label: "Matrícula" },
  { id: "health", label: "Saúde" },
  { id: "guardians", label: "Responsáveis" },
];

const BLOOD_TYPES = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];

type FieldErrors = Record<string, string>;

function genderOptions(): { value: string; label: string }[] {
  return Object.entries(MEMBER_GENDER_LABELS).map(([value, label]) => ({ value, label }));
}

function enrollmentTypeOptions(): { value: string; label: string }[] {
  return Object.entries(MEMBER_ENROLLMENT_TYPE_LABELS).map(([value, label]) => ({ value, label }));
}

function tabForError(key: string): TabId {
  if (key.startsWith("guardian-")) return "guardians";
  if (["member-plan", "member-enrollment-type", "member-enrollment-date"].includes(key)) return "enrollment";
  if (["member-blood-type", "member-weight", "member-height", "member-emergency-phone", "member-health-notes"].includes(key)) return "health";
  return "personal";
}

function addressErrors(errors: FieldErrors): Partial<Record<keyof AddressFormValue, string>> {
  return {
    zipCode: errors["member-address-zip-code"],
    street: errors["member-address-street"],
    number: errors["member-address-number"],
    complement: errors["member-address-complement"],
    neighborhood: errors["member-address-neighborhood"],
    city: errors["member-address-city"],
    state: errors["member-address-state"],
  };
}

export function MemberForm({
  submitting,
  submitError,
  plans,
  initial,
  editing,
  loading = false,
  loadError = null,
  onRetryLoad,
  onSubmit,
  onCancel,
}: MemberFormProps) {
  const [values, setValues] = useState<MemberFormValues>(() =>
    initial ? memberFormValuesFromProfile(initial) : emptyMemberFormValues(),
  );
  const [activeTab, setActiveTab] = useState<TabId>("personal");
  const [errors, setErrors] = useState<FieldErrors>({});
  const [focusTarget, setFocusTarget] = useState<string | null>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const isEditing = editing ?? Boolean(initial);
  const submitLabel = isEditing ? "Salvar alterações" : "Cadastrar aluno";
  const submittingLabel = isEditing ? "Salvando..." : "Cadastrando...";
  const title = isEditing ? "Editar aluno" : "Cadastrar aluno";

  useEffect(() => {
    if (!focusTarget) return;
    const element = document.getElementById(focusTarget);
    element?.focus();
    setFocusTarget(null);
  }, [activeTab, focusTarget]);

  const setField = <K extends keyof MemberFormValues>(key: K, value: MemberFormValues[K]) => {
    setValues(current => ({ ...current, [key]: value }));
  };

  const validate = (): FieldErrors => {
    const next: FieldErrors = {};
    const name = values.name.trim();
    const email = values.email.trim();
    const phone = values.phone.trim();

    if (name === "") next["member-name"] = "Informe o nome";
    else if (name.length > 120) next["member-name"] = "Nome deve ter no máximo 120 caracteres";
    if (!isValidCpf(values.cpf)) next["member-cpf"] = "Informe um CPF válido";
    if (email && !isValidEmail(email)) next["member-email"] = "Informe um e-mail válido";
    if (phone && !isValidPhone(phone)) next["member-phone"] = "Informe um telefone válido";
    if (!isValidDateOfBirth(values.dateOfBirth)) next["member-date-of-birth"] = "Informe uma data de nascimento válida";
    if (!values.gender) next["member-gender"] = "Selecione o gênero";

    const address = values.address;
    if (!isValidZipCode(address.zipCode)) next["member-address-zip-code"] = "Informe um CEP válido";
    if (!isValidState(address.state)) next["member-address-state"] = "Informe uma UF válida";
    if (!isValidNotes(values.notes)) next["member-notes"] = "Observações devem ter no máximo 1000 caracteres";

    if (!values.planId) next["member-plan"] = "Selecione o plano";
    if (!values.enrollmentType) next["member-enrollment-type"] = "Selecione a origem";
    if (!isValidEnrollmentDate(values.enrollmentDate)) next["member-enrollment-date"] = "Informe uma data de matrícula válida";

    const health = values.health;
    if (!isValidBloodType(health.bloodType)) next["member-blood-type"] = "Selecione um tipo sanguíneo válido";
    const emergencyPhone = health.emergencyContactPhone.trim();
    if (emergencyPhone && !isValidPhone(emergencyPhone)) next["member-emergency-phone"] = "Informe um telefone válido";
    if (!isValidWeightKg(values.measurements.weightKg)) next["member-weight"] = "Peso deve ser maior que 0 e no máximo 500 kg";
    if (!isValidHeightCm(values.measurements.heightCm)) next["member-height"] = "Altura deve ser maior que 0 e no máximo 300 cm";
    if (!isValidHealthNotes(health.notes)) next["member-health-notes"] = "Observações devem ter no máximo 1000 caracteres";

    values.guardians.forEach((guardian, index) => {
      if (!isValidGuardianName(guardian.name)) next[`guardian-${index}-name`] = "Informe o nome do responsável";
      const guardianPhone = guardian.phone.trim();
      if (guardianPhone && !isValidPhone(guardianPhone)) next[`guardian-${index}-phone`] = "Informe um telefone válido";
      if (!isValidZipCode(guardian.address.zipCode)) next[`guardian-${index}-zip-code`] = "Informe um CEP válido";
      if (!isValidState(guardian.address.state)) next[`guardian-${index}-state`] = "Informe uma UF válida";
      if (!isValidHealthNotes(guardian.notes)) next[`guardian-${index}-notes`] = "Observações devem ter no máximo 1000 caracteres";
    });

    return next;
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (submitting || loading) return;
    const nextErrors = validate();
    setErrors(nextErrors);
    const firstError = Object.keys(nextErrors)[0];
    if (firstError) {
      setActiveTab(tabForError(firstError));
      setFocusTarget(firstError);
      return;
    }
    onSubmit(buildMemberPayload(values, { editing: isEditing }));
  };

  const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    let nextIndex: number | null = null;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") nextIndex = (index + 1) % TABS.length;
    else if (event.key === "ArrowLeft" || event.key === "ArrowUp") nextIndex = (index - 1 + TABS.length) % TABS.length;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = TABS.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    setActiveTab(TABS[nextIndex]!.id);
    tabRefs.current[nextIndex]?.focus();
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontFamily: font,
    fontSize: 12,
    fontWeight: 600,
    color: C.gray,
    marginBottom: 4,
    marginTop: 12,
  };
  const errorStyle: React.CSSProperties = { color: C.red, fontSize: 11.5, marginTop: 4 };
  const sectionTitleStyle: React.CSSProperties = { color: C.ink, fontSize: 13, marginTop: 4 };

  const actions = (
    <div
      style={{
        position: "sticky",
        bottom: -18,
        display: "flex",
        justifyContent: "flex-end",
        gap: 8,
        marginTop: 18,
        padding: "12px 0",
        background: C.white,
        borderTop: `1px solid ${C.faint}`,
        zIndex: 2,
      }}
    >
      <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancelar</Button>
      {!loading && !loadError && <Button type="submit" disabled={submitting}>{submitting ? submittingLabel : submitLabel}</Button>}
    </div>
  );

  const header = (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
      <div id="member-form-title">
        <Label style={{ color: C.ink, fontSize: 13, letterSpacing: 0.5 }}>{title}</Label>
      </div>
      <Button variant="ghost" onClick={onCancel} disabled={submitting} ariaLabel="Fechar" style={{ fontSize: 16, lineHeight: 1 }}>
        ✕
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div role="dialog" aria-modal="true" aria-labelledby="member-form-title" style={{ position: "fixed", inset: 0, background: C.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
        <Card style={{ width: "100%", maxWidth: 760, maxHeight: "90vh", overflowY: "auto" }}>
          {header}
          <div role="status" style={{ color: C.light, fontSize: 13, padding: "24px 0" }}>Carregando dados do aluno...</div>
          {actions}
        </Card>
      </div>
    );
  }

  if (loadError) {
    return (
      <div role="dialog" aria-modal="true" aria-labelledby="member-form-title" style={{ position: "fixed", inset: 0, background: C.overlay, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
        <Card style={{ width: "100%", maxWidth: 760, maxHeight: "90vh", overflowY: "auto" }}>
          {header}
          <div role="alert" style={{ color: C.red, background: C.bg, borderRadius: 8, padding: 10, marginTop: 16, fontSize: 12.5 }}>
            {loadError}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 18 }}>
            <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancelar</Button>
            {onRetryLoad && <Button onClick={onRetryLoad} disabled={submitting}>Tentar novamente</Button>}
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="member-form-title"
      style={{
        position: "fixed",
        inset: 0,
        background: C.overlay,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
        padding: 16,
      }}
    >
      <Card style={{ width: "100%", maxWidth: 760, maxHeight: "90vh", overflowY: "auto" }}>
        {header}

        <div role="tablist" aria-label="Seções do cadastro de aluno" style={{ display: "flex", gap: 6, margin: "16px 0", flexWrap: "wrap" }}>
          {TABS.map((tab, index) => {
            const selected = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                ref={element => { tabRefs.current[index] = element; }}
                type="button"
                role="tab"
                id={`member-tab-${tab.id}`}
                aria-selected={selected}
                aria-controls={`member-tabpanel-${tab.id}`}
                tabIndex={selected ? 0 : -1}
                onClick={() => setActiveTab(tab.id)}
                onKeyDown={event => handleTabKeyDown(event, index)}
                style={{
                  fontFamily: font,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  padding: "6px 13px",
                  borderRadius: 20,
                  background: selected ? C.ink : C.white,
                  color: selected ? C.white : C.gray,
                  border: `1px solid ${selected ? C.ink : C.faint}`,
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div role="tabpanel" id="member-tabpanel-personal" aria-labelledby="member-tab-personal" tabIndex={0}>
            {activeTab === "personal" && (
              <>
                <label htmlFor="member-name" style={labelStyle}>Nome *</label>
                <FieldInput
                  id="member-name"
                  value={values.name}
                  onChange={event => setField("name", event.target.value)}
                  maxLength={121}
                  autoComplete="name"
                  disabled={submitting}
                />
                {errors["member-name"] && <div style={errorStyle}>{errors["member-name"]}</div>}

                <label htmlFor="member-cpf" style={labelStyle}>CPF *</label>
                <FieldInput
                  id="member-cpf"
                  value={values.cpf}
                  onChange={event => setField("cpf", event.target.value)}
                  placeholder="000.000.000-00"
                  inputMode="numeric"
                  autoComplete="off"
                  disabled={submitting}
                />
                {errors["member-cpf"] && <div style={errorStyle}>{errors["member-cpf"]}</div>}

                <label htmlFor="member-date-of-birth" style={labelStyle}>Data de nascimento *</label>
                <DateTimePicker
                  id="member-date-of-birth"
                  ariaLabel="Data de nascimento"
                  value={values.dateOfBirth}
                  mode="date"
                  placement="below"
                  minYear={1900}
                  maxYear={new Date().getFullYear()}
                  disabled={submitting}
                  onChange={value => setField("dateOfBirth", value)}
                />
                {errors["member-date-of-birth"] && <div style={errorStyle}>{errors["member-date-of-birth"]}</div>}

                <div style={labelStyle} id="member-gender-label">Gênero *</div>
                <Select
                  id="member-gender"
                  ariaLabel="Gênero"
                  value={values.gender}
                  options={genderOptions()}
                  placeholder="Selecione..."
                  disabled={submitting}
                  onChange={value => setField("gender", value)}
                />
                {errors["member-gender"] && <div style={errorStyle}>{errors["member-gender"]}</div>}

                <label htmlFor="member-email" style={labelStyle}>E-mail</label>
                <FieldInput
                  id="member-email"
                  type="email"
                  value={values.email}
                  onChange={event => setField("email", event.target.value)}
                  maxLength={254}
                  autoComplete="email"
                  disabled={submitting}
                />
                {errors["member-email"] && <div style={errorStyle}>{errors["member-email"]}</div>}

                <label htmlFor="member-phone" style={labelStyle}>Telefone</label>
                <FieldInput
                  id="member-phone"
                  type="tel"
                  value={values.phone}
                  onChange={event => setField("phone", event.target.value)}
                  placeholder="(11) 98765-4321"
                  autoComplete="tel"
                  disabled={submitting}
                />
                {errors["member-phone"] && <div style={errorStyle}>{errors["member-phone"]}</div>}

                <div style={{ ...sectionTitleStyle, marginTop: 22, fontWeight: 700 }}>Endereço (opcional)</div>
                <AddressFields
                  idPrefix="member-address"
                  value={values.address}
                  onChange={address => setValues(current => ({ ...current, address }))}
                  disabled={submitting}
                  errors={addressErrors(errors)}
                />

                <label htmlFor="member-notes" style={labelStyle}>Observações gerais</label>
                <FieldTextarea
                  id="member-notes"
                  value={values.notes}
                  onChange={event => setField("notes", event.target.value)}
                  maxLength={1000}
                  disabled={submitting}
                />
                <div style={{ color: C.light, fontSize: 11, textAlign: "right", marginTop: 2 }}>
                  {values.notes.length}/1000
                </div>
                {errors["member-notes"] && <div style={errorStyle}>{errors["member-notes"]}</div>}
              </>
            )}
          </div>

          <div role="tabpanel" id="member-tabpanel-enrollment" aria-labelledby="member-tab-enrollment" tabIndex={0}>
            {activeTab === "enrollment" && (
              <>
                <div id="member-plan-label" style={labelStyle}>Plano *</div>
                <Select
                  id="member-plan"
                  ariaLabel="Plano"
                  value={values.planId}
                  options={plans.map(plan => ({ value: plan.id, label: plan.name }))}
                  placeholder="Selecione..."
                  disabled={submitting}
                  onChange={value => setField("planId", value)}
                />
                {plans.length === 0 && (
                  <div style={{ color: C.light, fontSize: 11.5, marginTop: 4 }}>
                    Nenhum plano ativo disponível para matrícula.
                  </div>
                )}
                {errors["member-plan"] && <div style={errorStyle}>{errors["member-plan"]}</div>}

                <div style={labelStyle} id="member-enrollment-type-label">Origem *</div>
                <Select
                  id="member-enrollment-type"
                  ariaLabel="Origem"
                  value={values.enrollmentType}
                  options={enrollmentTypeOptions()}
                  placeholder="Selecione..."
                  disabled={submitting}
                  onChange={value => setField("enrollmentType", value)}
                />
                {errors["member-enrollment-type"] && <div style={errorStyle}>{errors["member-enrollment-type"]}</div>}

                <label htmlFor="member-enrollment-date" style={labelStyle}>Data de matrícula *</label>
                <DateTimePicker
                  id="member-enrollment-date"
                  ariaLabel="Data de matrícula"
                  value={values.enrollmentDate}
                  mode="date"
                  placement="above"
                  maxYear={new Date().getFullYear()}
                  disabled={submitting}
                  onChange={value => setField("enrollmentDate", value)}
                />
                {errors["member-enrollment-date"] && <div style={errorStyle}>{errors["member-enrollment-date"]}</div>}
              </>
            )}
          </div>

          <div role="tabpanel" id="member-tabpanel-health" aria-labelledby="member-tab-health" tabIndex={0}>
            {activeTab === "health" && (
              <>
                <div role="note" style={{ color: C.gray, background: C.bg, borderRadius: 8, padding: 10, fontSize: 11.5, marginBottom: 4 }}>
                  Dados de saúde são sensíveis: ficam visíveis apenas para a gestão do CT e não são enviados para telemetria.
                </div>

                <div style={labelStyle} id="member-blood-type-label">Tipo sanguíneo</div>
                <Select
                  id="member-blood-type"
                  ariaLabel="Tipo sanguíneo"
                  value={values.health.bloodType}
                  options={BLOOD_TYPES.map(value => ({ value, label: value }))}
                  placeholder="Selecione..."
                  disabled={submitting}
                  onChange={value => setField("health", { ...values.health, bloodType: value })}
                />
                {errors["member-blood-type"] && <div style={errorStyle}>{errors["member-blood-type"]}</div>}

                <label htmlFor="member-emergency-name" style={labelStyle}>Contato de emergência</label>
                <FieldInput
                  id="member-emergency-name"
                  value={values.health.emergencyContactName}
                  onChange={event => setField("health", { ...values.health, emergencyContactName: event.target.value })}
                  maxLength={120}
                  disabled={submitting}
                />

                <label htmlFor="member-emergency-phone" style={labelStyle}>Telefone de emergência</label>
                <FieldInput
                  id="member-emergency-phone"
                  type="tel"
                  value={values.health.emergencyContactPhone}
                  onChange={event => setField("health", { ...values.health, emergencyContactPhone: event.target.value })}
                  placeholder="(11) 98765-4321"
                  disabled={submitting}
                />
                {errors["member-emergency-phone"] && <div style={errorStyle}>{errors["member-emergency-phone"]}</div>}

                <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 12 }}>
                  <div>
                    <label htmlFor="member-weight" style={labelStyle}>Peso (kg)</label>
                    <FieldInput
                      id="member-weight"
                      inputMode="decimal"
                      value={values.measurements.weightKg}
                      onChange={event => setField("measurements", { ...values.measurements, weightKg: event.target.value })}
                      disabled={submitting}
                    />
                    {errors["member-weight"] && <div style={errorStyle}>{errors["member-weight"]}</div>}
                  </div>
                  <div>
                    <label htmlFor="member-height" style={labelStyle}>Altura (cm)</label>
                    <FieldInput
                      id="member-height"
                      inputMode="decimal"
                      value={values.measurements.heightCm}
                      onChange={event => setField("measurements", { ...values.measurements, heightCm: event.target.value })}
                      disabled={submitting}
                    />
                    {errors["member-height"] && <div style={errorStyle}>{errors["member-height"]}</div>}
                  </div>
                </div>

                <label htmlFor="member-health-notes" style={labelStyle}>Observações de saúde</label>
                <FieldTextarea
                  id="member-health-notes"
                  value={values.health.notes}
                  onChange={event => setField("health", { ...values.health, notes: event.target.value })}
                  maxLength={1000}
                  disabled={submitting}
                />
                <div style={{ color: C.light, fontSize: 11, textAlign: "right", marginTop: 2 }}>
                  {values.health.notes.length}/1000
                </div>
                {errors["member-health-notes"] && <div style={errorStyle}>{errors["member-health-notes"]}</div>}
              </>
            )}
          </div>

          <div role="tabpanel" id="member-tabpanel-guardians" aria-labelledby="member-tab-guardians" tabIndex={0}>
            {activeTab === "guardians" && (
              <>
                <div style={{ color: C.gray, fontSize: 12, marginTop: 4 }}>
                  Até {MAX_GUARDIANS} responsáveis por aluno. Bloco opcional.
                </div>
                <GuardianEditor
                  guardians={values.guardians}
                  onChange={guardians => setField("guardians", guardians)}
                  disabled={submitting}
                  errors={errors}
                  focusField={focusTarget}
                />
              </>
            )}
          </div>

          {submitError && (
            <div role="alert" style={{ ...errorStyle, background: C.bg, borderRadius: 8, padding: 10, marginTop: 14 }}>
              {submitError}
            </div>
          )}

          {actions}
        </form>
      </Card>
    </div>
  );
}
